/**
 * 지출결의등록(type1) 챗봇 — LLM 없이 한국어 규칙 파서.
 * CRUD 는 화면과 동일하게 localStorage 스토어만 사용한다.
 */

import {
  EVIDENCE_SOURCE,
  ensureEvidenceSeed,
  filterOpenEvidences,
  loadEvidences,
  detailFromEvidence,
  occupyEvidencesDraft,
  releaseEvidencesDraft,
  promoteEvidencesDraftToResolution,
  releaseEvidencesByResolution,
} from 'expense/expenseEvidenceStore';
import {
  ensureIncomeEarnerSeed,
  loadIncomeStatements,
  filterOpenStatements,
  detailFromIncomeStatement,
  occupyStatementsDraft,
  releaseStatementsDraft,
  promoteStatementsDraftToResolution,
  releaseStatementsByResolution,
} from 'expense/expenseIncomeStore';
import {
  TYPE1_DRAFT_NO_LABEL,
  loadType1Draft,
  saveType1Draft,
  clearType1Draft,
  linkBundleToDraft,
  summarizeDraftSession,
  buildResolutionFromDraft,
  isType1DraftRow,
  hasIssuedResolutionNo,
  draftSessionToListRow,
} from 'expense/resolutionType1DraftStore';
import {
  listProjectsAggregated,
  listSubjectsForSelectedProject,
} from 'expense/resolutionType1Utils';
import {
  EXPENSE_STATUS,
  expenseStatusLabel,
  createResolutionDetailUid,
  emptyResolutionDetailRow,
  loadResolutionBundle,
  saveResolutionBundle,
  loadObligationBundle,
  saveObligationBundle,
  ensureObligationForResolution,
  filterResolutionsForRegister,
  requestResolutionApproval,
} from 'expense/expenseStore';

export const TYPE1_CHAT_SCREEN_ID = 'EXP_RES_REG_T1';
const PREVIEW_TTL_MS = 10 * 60 * 1000;

/** @type {Map<string, { expiresAt: number, tenantCode: string, userKey: string, action: string, payload: object }>} */
const pendingPreviews = new Map();
/** @type {Map<string, object>} */
const slotSessions = new Map();

const KIND = {
  HELP: 'HELP',
  LIST: 'LIST',
  LIST_EVIDENCE: 'LIST_EVIDENCE',
  LIST_PROJECT: 'LIST_PROJECT',
  LIST_SUBJECT: 'LIST_SUBJECT',
  LINK: 'LINK',
  ADD_PAYEE: 'ADD_PAYEE',
  SAVE: 'SAVE',
  DELETE: 'DELETE',
  APPROVE: 'APPROVE',
  REFUSE_TENANT: 'REFUSE_TENANT',
  UNKNOWN: 'UNKNOWN',
};

const HELP_TEXT =
  '로그인 기관 지출결의(type1)만 다룹니다. LLM 없이 동작합니다.\n' +
  '· 결의 목록 보여줘\n' +
  '· 미결 증빙 보여줘 / 사업 목록 / 과목 목록\n' +
  '· 사용일 20260130 법인카드사용내역을 채주내역에 추가해줘\n' +
  '· 스타벅스 증빙을 문화 사업 과목 210에 연결해줘\n' +
  '· 채주 홍길동 50,000원 추가해줘\n' +
  '· 임시저장 저장해줘\n' +
  '· 임시저장 삭제해줘\n' +
  '· 결의번호 2026-1-1 삭제해줘 / 승인요청\n' +
  '쓰기는 미리보기 후 적용해야 반영됩니다.';

function now() {
  return Date.now();
}

function sessionKey(ctx) {
  return [ctx.tenantCode || '', ctx.draftUserKey || '', ctx.fiscalYear || ''].join('|');
}

function getSlots(ctx) {
  return slotSessions.get(sessionKey(ctx)) || null;
}

function setSlots(ctx, value) {
  const key = sessionKey(ctx);
  if (!value) slotSessions.delete(key);
  else slotSessions.set(key, value);
}

function purgeExpired() {
  const t = now();
  for (const [id, p] of pendingPreviews.entries()) {
    if (p.expiresAt < t) pendingPreviews.delete(id);
  }
}

function base(ctx) {
  return {
    success: true,
    screenId: TYPE1_CHAT_SCREEN_ID,
    tenantCode: ctx.tenantCode || '',
  };
}

function reply(ctx, message, extra = {}) {
  return { ...base(ctx), type: extra.type || 'reply', message, ...extra };
}

function help(ctx, prefix = '') {
  return reply(ctx, (prefix ? `${prefix}\n\n` : '') + HELP_TEXT, { type: 'help' });
}

function str(v) {
  return v == null ? '' : String(v).trim();
}

function norm(v) {
  return str(v).toLowerCase().replace(/\s+/g, '');
}

function money(n) {
  return `${(Number(n) || 0).toLocaleString()}원`;
}

function containsAny(text, ...words) {
  const list = words.length === 1 && Array.isArray(words[0]) ? words[0] : words;
  return list.some((w) => typeof w === 'string' && text.includes(w));
}

function stripJosa(s) {
  return str(s).replace(/(을|를|이|가|은|는|에|로|으로|만|도)$/, '');
}

function capture(re, text, group = 1) {
  const m = text.match(re);
  return m ? stripJosa(m[group]) : '';
}

/** 사용일 → YYYYMMDD (20260130, 2026-01-30, 2026년1월30일) */
function parseUseDate(text) {
  const raw = str(text);
  const pad = (y, m, d) => {
    const yy = Number(y);
    const mm = Number(m);
    const dd = Number(d);
    if (!yy || mm < 1 || mm > 12 || dd < 1 || dd > 31) return '';
    return `${yy}${String(mm).padStart(2, '0')}${String(dd).padStart(2, '0')}`;
  };
  const labeled8 = raw.match(/사용일\s*[:：은는]?\s*(\d{8})\b/);
  if (labeled8) return labeled8[1];
  const labeledKo = raw.match(
    /사용일\s*[:：은는]?\s*(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일?/
  );
  if (labeledKo) return pad(labeledKo[1], labeledKo[2], labeledKo[3]);
  const ko = raw.match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
  if (ko) return pad(ko[1], ko[2], ko[3]);
  const iso = raw.match(/\b(\d{4})[-./](\d{1,2})[-./](\d{1,2})\b/);
  if (iso) return pad(iso[1], iso[2], iso[3]);
  const compact = raw.match(/\b(20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\b/);
  if (compact) return compact[1] + compact[2] + compact[3];
  return '';
}

function dateKey(v) {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 8 ? d : '';
}

function formatUseDate(ymd) {
  const d = dateKey(ymd);
  if (d.length !== 8) return str(ymd);
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}

function parseAmount(text) {
  const man = text.match(/(\d+(?:\.\d+)?)\s*만(?:\s*(\d+)\s*천)?/);
  if (man) {
    const n = Number(man[1]) * 10000 + (man[2] ? Number(man[2]) * 1000 : 0);
    if (n > 0) return n;
  }
  const won = text.match(/(\d{1,3}(?:,\d{3})+|\d+)\s*원/);
  if (won) {
    const n = Number(String(won[1]).replace(/,/g, ''));
    if (n > 0) return n;
  }
  const labeled = text.match(/금액\s*[:：은는]?\s*(\d{1,3}(?:,\d{3})+|\d+)/);
  if (labeled) {
    const n = Number(String(labeled[1]).replace(/,/g, ''));
    if (n > 0) return n;
  }
  return null;
}

function parseIntent(raw) {
  const text = str(raw);
  const intent = {
    kind: KIND.UNKNOWN,
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    deptCode: '',
    deptName: '',
    evidenceKeyword: '',
    evidenceSource: '',
    useDate: '',
    vendorName: '',
    amount: null,
    resolutionNo: '',
    keyword: '',
    useScreenPayees: false,
    pickIndex: null,
  };
  if (!text) {
    intent.kind = KIND.HELP;
    return intent;
  }

  if (/(다른\s*기관|기관코드\s*[:：=]|테넌트\s*바꿔|compCd\s*=)/i.test(text)) {
    intent.kind = KIND.REFUSE_TENANT;
    return intent;
  }

  const n = text.replace(/\s+/g, '');
  const pick = text.match(/^(\d{1,2})\s*번?$/);
  if (pick) intent.pickIndex = Number(pick[1]);

  intent.projectCode = capture(/(?:사업코드)\s*[:：은는]?\s*([A-Za-z0-9._-]+)/, text);
  intent.projectName = capture(/(?:사업명|사업)\s*[:：은는]?\s*[「『"']?([^「『"'\s,，]+)/, text);
  if (!intent.projectName) {
    intent.projectName = capture(/([가-힣A-Za-z0-9._-]{2,40})\s*사업/, text);
  }
  intent.subjectCode = capture(/(?:과목코드)\s*[:：은는]?\s*([A-Za-z0-9._-]+)/, text);
  intent.subjectName = capture(/(?:과목명|과목)\s*[:：은는]?\s*[「『"']?([^「『"'\s,，]+)/, text);
  intent.deptCode = capture(/(?:부서코드)\s*[:：은는]?\s*([A-Za-z0-9._-]+)/, text);
  intent.deptName = capture(/(?:부서명|부서)\s*[:：은는]?\s*[「『"']?([^「『"'\s,，]+)/, text);
  intent.vendorName =
    capture(/(?:채주명)\s*[:：은는]?\s*[「『"']?([^「『"'\s,，]+)/, text) ||
    capture(/채주\s+([가-힣A-Za-z0-9._-]{2,40})/, text);
  if (intent.vendorName && /^(내역|데이터|사용내역)$/.test(intent.vendorName)) {
    intent.vendorName = '';
  }
  intent.resolutionNo = capture(/(?:결의번호)\s*[:：은는]?\s*([A-Za-z0-9._-]+)/, text);
  intent.amount = parseAmount(text);
  intent.useDate = parseUseDate(text);

  intent.evidenceKeyword =
    capture(/(?:가맹점|승인번호)\s*[:：은는]?\s*[「『"']?([^「『"'\s,，]+)/, text) ||
    capture(/([가-힣A-Za-z0-9._-]{2,40})\s*(?:증빙|가맹점)/, text);
  if (text.includes('세금계산서')) intent.evidenceSource = EVIDENCE_SOURCE.TAX;
  else if (containsAny(n, '법인카드', '카드사용')) intent.evidenceSource = EVIDENCE_SOURCE.CARD;
  else if (text.includes('카드')) intent.evidenceSource = EVIDENCE_SOURCE.CARD;

  if (containsAny(n, '지금채주', '화면채주', '채주연결') || /지금\s*(화면)?\s*채주/.test(text)) {
    intent.useScreenPayees = true;
  }

  if (n.includes('도움') || n.toLowerCase() === 'help' || n.includes('어떻게') || n.includes('예시')) {
    intent.kind = KIND.HELP;
    return intent;
  }
  if (containsAny(n, '삭제', '폐지', '없애', '취소해')) {
    intent.kind = KIND.DELETE;
    return intent;
  }
  if (containsAny(n, '승인요청', '결재올려', 'gw연동')) {
    intent.kind = KIND.APPROVE;
    return intent;
  }
  if (containsAny(n, '연결', '묶어', '붙여')) {
    intent.kind = KIND.LINK;
    if (!intent.evidenceKeyword && !intent.vendorName) {
      const used = new Set(
        [intent.projectName, intent.projectCode, intent.subjectName, intent.subjectCode, intent.deptName].map(norm)
      );
      const skip = /^(연결|묶어|붙여|해줘|주세요|으로|로|과목|사업|증빙|채주|임시|저장)$/;
      for (const rawTok of text.split(/[\s,，]+/)) {
        const t = stripJosa(rawTok);
        if (t.length < 2 || skip.test(t) || used.has(norm(t))) continue;
        if (/^\d+$/.test(t)) continue;
        intent.evidenceKeyword = t;
        break;
      }
    }
    return intent;
  }
  if (
    containsAny(n, '채주추가', '수기') ||
    (n.includes('채주') && containsAny(n, '추가', '등록', '넣어', '불러'))
  ) {
    intent.kind = KIND.ADD_PAYEE;
    return intent;
  }
  if (containsAny(n, '채번', '결의로저장') || (containsAny(n, '저장', '등록해') && containsAny(n, '임시', '결의', '채번'))) {
    intent.kind = KIND.SAVE;
    return intent;
  }
  if (n.includes('저장해') || n === '저장') {
    intent.kind = KIND.SAVE;
    return intent;
  }
  if (containsAny(n, '미결', '증빙목록', '카드내역', '세금계산서') && containsAny(n, '목록', '보여', '조회', '검색')) {
    intent.kind = KIND.LIST_EVIDENCE;
    intent.keyword = capture(/(?:검색|조회|찾아)\s*[「『"']?([^「『"'\s]{1,40})/, text);
    return intent;
  }
  if (containsAny(n, '과목목록', '과목내역') || (n.includes('과목') && containsAny(n, '목록', '보여', '조회'))) {
    intent.kind = KIND.LIST_SUBJECT;
    return intent;
  }
  if (containsAny(n, '사업목록', '사업내역') || (n.includes('사업') && containsAny(n, '목록', '보여', '조회'))) {
    intent.kind = KIND.LIST_PROJECT;
    return intent;
  }
  if (containsAny(n, '조회', '목록', '보여', '검색', '찾아', '뭐있')) {
    intent.kind = KIND.LIST;
    intent.keyword =
      capture(/(?:검색|조회|찾아)\s*[「『"']?([^「『"'\s]{1,40})/, text) ||
      intent.projectName ||
      intent.deptName ||
      intent.resolutionNo;
    return intent;
  }

  return intent;
}

function scoreRow(row, keyword, fields) {
  const k = norm(keyword);
  if (!k) return 0;
  let best = 0;
  for (const f of fields) {
    const v = norm(row?.[f]);
    if (!v) continue;
    if (v === k) best = Math.max(best, 100);
    else if (v.includes(k) || k.includes(v)) best = Math.max(best, 70);
  }
  return best;
}

function rankRows(rows, keyword, fields, limit = 6) {
  return (rows || [])
    .map((row) => ({ row, score: scoreRow(row, keyword, fields) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/** 1건이면 추천, 여러 건이면 후보 */
function resolveClosest(rows, keyword, fields, label) {
  const k = str(keyword);
  if (!k) return { hit: null, candidates: [], note: '', ask: `${label}을(를) 알려 주세요.` };
  const ranked = rankRows(rows, k, fields);
  if (!ranked.length) {
    return { hit: null, candidates: [], note: '', ask: `「${k}」에 해당하는 ${label}을 찾지 못했습니다.` };
  }
  if (ranked.length === 1 || ranked[0].score >= 100) {
    const hit = ranked[0].row;
    const note =
      ranked[0].score < 100 ? `가장 가까운 ${label}은 「${formatHit(hit, fields)}」입니다.` : '';
    return { hit, candidates: [], note, ask: '' };
  }
  const top = ranked[0].score;
  const close = ranked.filter((x) => top - x.score <= 15);
  if (close.length === 1) {
    return {
      hit: close[0].row,
      candidates: [],
      note: `가장 가까운 ${label}은 「${formatHit(close[0].row, fields)}」입니다.`,
      ask: '',
    };
  }
  return {
    hit: null,
    candidates: close.map((x) => x.row),
    note: '',
    ask: `가까운 ${label}이 여러 건입니다. 번호를 말해 주세요.`,
  };
}

function formatHit(row, fields) {
  const a = str(row?.[fields[0]]);
  const b = str(row?.[fields[1]]);
  return [a, b].filter(Boolean).join(' ');
}

function formatCandidateList(rows, fields) {
  return (rows || [])
    .map((r, i) => `${i + 1}. ${formatHit(r, fields)}`)
    .join('\n');
}

function dimsOf(ctx) {
  return { tenantCode: ctx.tenantCode, fiscalYear: ctx.fiscalYear };
}

function loadProjects(ctx) {
  return listProjectsAggregated(ctx.tenantCode, ctx.fiscalYear, {
    includeFund: !!ctx.includeFund,
    deptCode: ctx.searchDeptCode || '',
  });
}

function loadSubjects(ctx, project) {
  const p = project || ctx.selectedProject;
  if (!p?.projectCode) return [];
  return listSubjectsForSelectedProject(ctx.tenantCode, ctx.fiscalYear, p);
}

function loadOpenEvidences(ctx, source) {
  ensureEvidenceSeed(ctx.tenantCode, ctx.fiscalYear);
  return filterOpenEvidences(loadEvidences(ctx.tenantCode, ctx.fiscalYear), source ? { source } : {});
}

function loadOpenStatements(ctx) {
  ensureIncomeEarnerSeed({ tenantCode: ctx.tenantCode });
  return filterOpenStatements(loadIncomeStatements(dimsOf(ctx)));
}

async function loadResultRows(ctx) {
  const bundle = await loadResolutionBundle(dimsOf(ctx));
  const draft = loadType1Draft(dimsOf(ctx), ctx.draftUserKey);
  const masters = filterResolutionsForRegister(bundle.masters || [], {
    deptCode: ctx.searchDeptCode || undefined,
  });
  const real = masters.map((m) => ({
    ...m,
    statusNm: expenseStatusLabel(m.status),
  }));
  const draftRow = draftSessionToListRow(draft);
  return draftRow ? [draftRow, ...real] : real;
}

function applyPick(intent, slots) {
  if (intent.pickIndex == null || !slots?.candidates?.length) return { intent, slots, picked: false };
  const idx = intent.pickIndex - 1;
  const row = slots.candidates[idx];
  if (!row) return { intent, slots, picked: false, error: '목록에 없는 번호입니다.' };
  const next = { ...intent };
  const type = slots.pickType;
  if (type === 'project') {
    next.projectCode = str(row.projectCode) || next.projectCode;
    next.projectName = str(row.projectName) || next.projectName;
    slots.project = row;
  } else if (type === 'subject') {
    next.subjectCode = str(row.subjectCode) || next.subjectCode;
    next.subjectName = str(row.subjectName) || next.subjectName;
    slots.subject = row;
  } else if (type === 'evidence') {
    next.evidenceKeyword = str(row.merchantName || row.approveNo);
    slots.evidences = [row];
  } else if (type === 'statement') {
    slots.statements = [row];
  } else if (type === 'resolution') {
    next.resolutionNo = str(row.resolutionNo);
    slots.resolution = row;
  }
  slots.candidates = [];
  slots.pickType = '';
  return { intent: next, slots, picked: true };
}

function mergeIntent(intent, slots) {
  if (!slots) return intent;
  return {
    ...intent,
    projectCode: intent.projectCode || slots.projectCode || '',
    projectName: intent.projectName || slots.projectName || '',
    subjectCode: intent.subjectCode || slots.subjectCode || '',
    subjectName: intent.subjectName || slots.subjectName || '',
    vendorName: intent.vendorName || slots.vendorName || '',
    amount: intent.amount != null ? intent.amount : slots.amount,
    evidenceKeyword: intent.evidenceKeyword || slots.evidenceKeyword || '',
    evidenceSource: intent.evidenceSource || slots.evidenceSource || '',
    useDate: intent.useDate || slots.useDate || '',
    resolutionNo: intent.resolutionNo || slots.resolutionNo || '',
    useScreenPayees: intent.useScreenPayees || !!slots.useScreenPayees,
    kind: intent.kind === KIND.UNKNOWN && slots.kind ? slots.kind : intent.kind,
  };
}

function persistSlots(ctx, intent, extra = {}) {
  setSlots(ctx, {
    kind: intent.kind,
    projectCode: intent.projectCode,
    projectName: intent.projectName,
    subjectCode: intent.subjectCode,
    subjectName: intent.subjectName,
    vendorName: intent.vendorName,
    amount: intent.amount,
    evidenceKeyword: intent.evidenceKeyword,
    evidenceSource: intent.evidenceSource,
    useDate: intent.useDate,
    resolutionNo: intent.resolutionNo,
    useScreenPayees: intent.useScreenPayees,
    project: extra.project || getSlots(ctx)?.project || null,
    subject: extra.subject || getSlots(ctx)?.subject || null,
    evidences: extra.evidences || getSlots(ctx)?.evidences || null,
    statements: extra.statements || getSlots(ctx)?.statements || null,
    payees: extra.payees || getSlots(ctx)?.payees || null,
    candidates: extra.candidates || [],
    pickType: extra.pickType || '',
  });
}

function preview(ctx, action, payload, headline) {
  purgeExpired();
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `pv-${now().toString(36)}`;
  pendingPreviews.set(id, {
    expiresAt: now() + PREVIEW_TTL_MS,
    tenantCode: ctx.tenantCode,
    userKey: ctx.draftUserKey,
    action,
    payload,
  });
  setSlots(ctx, null);
  return {
    ...base(ctx),
    type: 'preview',
    previewId: id,
    action,
    message: headline,
  };
}

function formatList(title, lines) {
  if (!lines.length) return `${title}\n(없음)`;
  const max = 40;
  const body = lines.slice(0, max).join('\n');
  const more = lines.length > max ? `\n… 외 ${lines.length - max}건` : '';
  return `${title} · ${lines.length}건\n${body}${more}`;
}

async function listResolutions(ctx, intent) {
  const rows = await loadResultRows(ctx);
  const kw = intent.keyword || intent.projectName || intent.deptName || intent.resolutionNo;
  const filtered = kw
    ? rows.filter((r) =>
        [r.resolutionNo, r.projectCode, r.projectName, r.deptName, r.subjectCode, r.remark]
          .some((v) => norm(v).includes(norm(kw)))
      )
    : rows;
  const lines = filtered.map((r) => {
    const amt = money(r.resolutionAmt);
    return `${r.resolutionNo || '-'} ${r.projectCode || ''} ${r.projectName || ''} ${amt} (${r.statusNm || r.status || '-'})`;
  });
  return reply(ctx, formatList('결의내역', lines), { type: 'list', rows: filtered });
}

function listEvidence(ctx, intent) {
  let rows = loadOpenEvidences(ctx, intent.evidenceSource);
  if (intent.useDate) {
    rows = rows.filter((r) => dateKey(r.useDate) === intent.useDate);
  }
  const kw = intent.keyword || intent.evidenceKeyword;
  const filtered = kw
    ? rows.filter((r) =>
        [r.merchantName, r.approveNo, r.cardNo, r.businessNo, r.remark].some((v) =>
          norm(v).includes(norm(kw))
        )
      )
    : rows;
  const title = intent.useDate
    ? `미결 증빙 (사용일 ${formatUseDate(intent.useDate)})`
    : '미결 증빙';
  const lines = filtered.map((r) => {
    const src = r.source === EVIDENCE_SOURCE.TAX ? '세금' : '카드';
    return `[${src}] ${r.useDate || ''} ${r.merchantName || ''} ${money(r.amount)} ${r.approveNo || ''}`;
  });
  return reply(ctx, formatList(title, lines), { type: 'list', rows: filtered });
}

function existingPayeeEvidenceKeys(ctx) {
  return new Set(
    (ctx.payees || [])
      .filter((r) => r && r.mod !== 'D')
      .map((r) => str(r.evidenceUid || r.statementUid))
      .filter(Boolean)
  );
}

/** 사용일(+소스)로 미결 전자증빙 */
function findOpenEvidencesByUseDate(ctx, intent) {
  const ymd = dateKey(intent.useDate);
  if (!ymd) return [];
  const source = intent.evidenceSource || EVIDENCE_SOURCE.CARD;
  return loadOpenEvidences(ctx, source).filter((r) => dateKey(r.useDate) === ymd);
}

function evidencesToPayees(evidences = []) {
  return (evidences || []).map((e) => ({
    ...detailFromEvidence(e, ''),
    detailUid: createResolutionDetailUid(),
    id: e.evidenceUid || createResolutionDetailUid(),
    mod: 'A',
  }));
}

function listProjects(ctx, intent) {
  const rows = loadProjects(ctx);
  const kw = intent.keyword || intent.projectName || intent.projectCode;
  const filtered = kw ? rankRows(rows, kw, ['projectCode', 'projectName', 'deptName']).map((x) => x.row) : rows;
  const lines = (filtered.length ? filtered : rows).map(
    (r) => `${r.projectCode} ${r.projectName || ''} (${r.deptCode} ${r.deptName || ''})`
  );
  return reply(ctx, formatList('사업내역', lines), { type: 'list', rows: filtered.length ? filtered : rows });
}

function listSubjects(ctx, intent) {
  const projects = loadProjects(ctx);
  let project = ctx.selectedProject;
  const pk = intent.projectCode || intent.projectName;
  if (pk) {
    const found = resolveClosest(projects, pk, ['projectCode', 'projectName'], '사업');
    if (found.ask && !found.hit) return reply(ctx, found.ask + (found.candidates?.length ? `\n${formatCandidateList(found.candidates, ['projectCode', 'projectName'])}` : ''));
    project = found.hit || project;
  }
  if (!project?.projectCode) {
    return reply(ctx, '과목을 보려면 사업을 먼저 알려 주세요.\n예) 문화 사업 과목 목록');
  }
  const rows = loadSubjects(ctx, project);
  const lines = rows.map(
    (r) => `${r.subjectCode} ${r.subjectName || ''} 가용 ${money(r.availableAmt ?? r.balanceAmt)}`
  );
  return reply(
    ctx,
    formatList(`과목내역 (${project.projectCode} ${project.projectName || ''})`, lines),
    { type: 'list', rows }
  );
}

function buildManualPayee(intent) {
  if (!intent.vendorName || !(Number(intent.amount) > 0)) return null;
  return emptyResolutionDetailRow({
    detailUid: createResolutionDetailUid(),
    vendorName: intent.vendorName,
    amount: Number(intent.amount),
    evidenceType: '수기',
    remark: intent.vendorName,
    mod: 'A',
  });
}

function resolvePayees(ctx, intent, slots) {
  if (slots?.payees?.length) return { payees: slots.payees, note: '' };
  if (slots?.evidences?.length) {
    return { payees: slots.evidences.map((e) => detailFromEvidence(e, '')), note: '' };
  }
  if (slots?.statements?.length) {
    return { payees: slots.statements.map((s) => detailFromIncomeStatement(s, '')), note: '' };
  }

  if (intent.useScreenPayees || (!intent.evidenceKeyword && !intent.vendorName && (ctx.payees || []).length)) {
    const payees = (ctx.payees || []).filter((r) => r && r.mod !== 'D');
    if (payees.length) {
      return { payees, note: '화면 채주내역을 사용합니다.', fromScreen: true };
    }
  }

  if (intent.evidenceKeyword) {
    const ev = resolveClosest(
      loadOpenEvidences(ctx, intent.evidenceSource),
      intent.evidenceKeyword,
      ['merchantName', 'approveNo', 'cardNo', 'businessNo'],
      '증빙'
    );
    if (ev.hit) {
      return { payees: [detailFromEvidence(ev.hit, '')], note: ev.note, evidences: [ev.hit] };
    }
    const st = resolveClosest(
      loadOpenStatements(ctx),
      intent.evidenceKeyword,
      ['earnerName', 'earnerCode', 'remark'],
      '소득명세'
    );
    if (st.hit) {
      return {
        payees: [detailFromIncomeStatement(st.hit, '')],
        note: st.note,
        statements: [st.hit],
      };
    }
    if (ev.candidates?.length) {
      return { ask: `${ev.ask}\n${formatCandidateList(ev.candidates, ['merchantName', 'approveNo'])}`, pickType: 'evidence', candidates: ev.candidates };
    }
    if (st.candidates?.length) {
      return { ask: `${st.ask}\n${formatCandidateList(st.candidates, ['earnerName', 'earnerCode'])}`, pickType: 'statement', candidates: st.candidates };
    }
    return { ask: ev.ask || '연결할 증빙을 찾지 못했습니다. 가맹점·승인번호 또는 「미결 증빙 보여줘」로 확인해 주세요.' };
  }

  const manual = buildManualPayee(intent);
  if (manual) return { payees: [manual], note: '수기 채주로 연결합니다.' };

  return {
    ask: '연결할 채주가 없습니다. 가맹점(증빙)을 말하거나, 채주명과 금액을 알려 주세요.\n예) 스타벅스 연결 / 채주 홍길동 50000원 연결',
  };
}

function resolveProject(ctx, intent, slots) {
  if (slots?.project?.projectCode) return { hit: slots.project, note: '' };
  const kw = intent.projectCode || intent.projectName;
  if (!kw && ctx.selectedProject?.projectCode) {
    return { hit: ctx.selectedProject, note: `선택된 사업 「${ctx.selectedProject.projectCode} ${ctx.selectedProject.projectName || ''}」을 사용합니다.` };
  }
  const rows = loadProjects(ctx);
  if (!kw) {
    if (rows.length === 1) {
      return { hit: rows[0], note: `사업이 1건이라 「${rows[0].projectCode} ${rows[0].projectName || ''}」로 잡았습니다.` };
    }
    return { ask: '사업코드 또는 사업명을 알려 주세요.', candidates: rows.slice(0, 8), pickType: 'project' };
  }
  const found = resolveClosest(rows, kw, ['projectCode', 'projectName', 'deptName'], '사업');
  if (found.candidates?.length) {
    return { ask: `${found.ask}\n${formatCandidateList(found.candidates, ['projectCode', 'projectName'])}`, candidates: found.candidates, pickType: 'project' };
  }
  return found;
}

function resolveSubject(ctx, intent, project, slots) {
  if (slots?.subject?.subjectCode) return { hit: slots.subject, note: '' };
  const rows = loadSubjects(ctx, project);
  const kw = intent.subjectCode || intent.subjectName;
  if (!kw) {
    if (rows.length === 1) {
      return { hit: rows[0], note: `과목이 1건이라 「${rows[0].subjectCode} ${rows[0].subjectName || ''}」로 잡았습니다.` };
    }
    if (!rows.length) return { ask: '이 사업에 편성된 과목이 없습니다.' };
    return {
      ask: '과목코드 또는 과목명을 알려 주세요.',
      candidates: rows.slice(0, 8),
      pickType: 'subject',
    };
  }
  const found = resolveClosest(rows, kw, ['subjectCode', 'subjectName'], '과목');
  if (found.candidates?.length) {
    return { ask: `${found.ask}\n${formatCandidateList(found.candidates, ['subjectCode', 'subjectName'])}`, candidates: found.candidates, pickType: 'subject' };
  }
  return found;
}

function previewLink(ctx, intent) {
  const slots = getSlots(ctx) || {};
  const notes = [];

  const projectRes = resolveProject(ctx, intent, slots);
  if (projectRes.ask) {
    persistSlots(ctx, intent, { candidates: projectRes.candidates, pickType: projectRes.pickType || 'project', project: projectRes.hit });
    const extra = projectRes.candidates?.length && !String(projectRes.ask).includes('\n')
      ? `\n${formatCandidateList(projectRes.candidates, ['projectCode', 'projectName'])}`
      : '';
    return reply(ctx, projectRes.ask + extra);
  }
  if (projectRes.note) notes.push(projectRes.note);
  const project = projectRes.hit;

  const subjectRes = resolveSubject(ctx, intent, project, slots);
  if (subjectRes.ask) {
    persistSlots(ctx, intent, { project, candidates: subjectRes.candidates, pickType: subjectRes.pickType || 'subject' });
    const extra = subjectRes.candidates?.length && !String(subjectRes.ask).includes('\n')
      ? `\n${formatCandidateList(subjectRes.candidates, ['subjectCode', 'subjectName'])}`
      : '';
    return reply(ctx, subjectRes.ask + extra);
  }
  if (subjectRes.note) notes.push(subjectRes.note);
  const subject = subjectRes.hit;

  const payeeRes = resolvePayees(ctx, intent, slots);
  if (payeeRes.ask) {
    persistSlots(ctx, intent, {
      project,
      subject,
      candidates: payeeRes.candidates,
      pickType: payeeRes.pickType,
      evidences: payeeRes.evidences,
      statements: payeeRes.statements,
    });
    return reply(ctx, payeeRes.ask);
  }
  if (payeeRes.note) notes.push(payeeRes.note);
  const payees = payeeRes.payees;

  const draft = loadType1Draft(dimsOf(ctx), ctx.draftUserKey);
  const linked = linkBundleToDraft(draft, {
    project: { ...project, fiscalYear: ctx.fiscalYear, tenantCode: ctx.tenantCode },
    subject,
    payees,
  });
  if (!linked.ok) {
    return reply(ctx, linked.error || '연결할 수 없습니다.');
  }

  const sum = summarizeDraftSession(linked.session);
  const payeeLines = payees
    .map((p) => `  · ${p.vendorName || '-'} ${money(p.amount)}`)
    .join('\n');
  const hint = notes.filter(Boolean).join('\n');
  const headline =
    (hint ? `${hint}\n\n` : '') +
    `다음 묶음을 임시저장에 연결합니다. 적용을 누르면 반영됩니다.\n` +
    `사업 ${project.projectCode} ${project.projectName || ''}\n` +
    `과목 ${subject.subjectCode} ${subject.subjectName || ''}\n` +
    `채주 ${payees.length}건\n${payeeLines}\n` +
    `누적 묶음 ${sum.bundles.length}개 · 합계 ${money(sum.resolutionAmt)}`;

  return preview(
    ctx,
    'LINK',
    {
      project,
      subject,
      payees,
      fromScreen: !!payeeRes.fromScreen,
    },
    headline
  );
}

function previewAddPayee(ctx, intent) {
  const slots = getSlots(ctx) || {};
  const wantEvidence =
    !!intent.useDate ||
    intent.evidenceSource === EVIDENCE_SOURCE.CARD ||
    intent.evidenceSource === EVIDENCE_SOURCE.TAX ||
    !!intent.evidenceKeyword ||
    !!(slots.evidences || []).length;

  if (wantEvidence && !intent.vendorName) {
    if (!intent.useDate && !intent.evidenceKeyword) {
      persistSlots(ctx, { ...intent, kind: KIND.ADD_PAYEE, evidenceSource: intent.evidenceSource || EVIDENCE_SOURCE.CARD });
      return reply(
        ctx,
        '사용일을 알려 주세요.\n예) 사용일 20260130 법인카드사용내역을 채주내역에 추가해줘'
      );
    }
    let hits = [];
    if ((slots.evidences || []).length) {
      hits = slots.evidences;
    } else if (intent.useDate) {
      hits = findOpenEvidencesByUseDate(ctx, intent);
    }
    if (!hits.length && intent.evidenceKeyword) {
      const ev = resolveClosest(
        loadOpenEvidences(ctx, intent.evidenceSource || EVIDENCE_SOURCE.CARD),
        intent.evidenceKeyword,
        ['merchantName', 'approveNo', 'cardNo', 'businessNo'],
        '증빙'
      );
      if (ev.hit) hits = [ev.hit];
      else if (ev.candidates?.length) {
        persistSlots(ctx, intent, { candidates: ev.candidates, pickType: 'evidence' });
        return reply(ctx, `${ev.ask}\n${formatCandidateList(ev.candidates, ['merchantName', 'approveNo'])}`);
      }
    }
    const srcLabel = intent.evidenceSource === EVIDENCE_SOURCE.TAX ? '세금계산서' : '법인카드';
    const dateLabel = intent.useDate ? `사용일 ${formatUseDate(intent.useDate)} ` : '';
    if (!hits.length) {
      return reply(ctx, `${dateLabel}미결 ${srcLabel} 사용내역이 없습니다.`);
    }
    const exist = existingPayeeEvidenceKeys(ctx);
    const fresh = hits.filter((e) => e.evidenceUid && !exist.has(String(e.evidenceUid)));
    if (!fresh.length) {
      return reply(ctx, `${dateLabel}${srcLabel} ${hits.length}건은 이미 채주내역에 있습니다.`);
    }
    const payees = evidencesToPayees(fresh);
    const lines = payees
      .map((p) => `  · ${p.vendorName || '-'} ${money(p.amount)} ${p.evidenceNo || ''}`)
      .join('\n');
    const skipped = hits.length - fresh.length;
    return preview(
      ctx,
      'ADD_PAYEE',
      { payees },
      `${dateLabel}${srcLabel} ${fresh.length}건을 채주내역에 추가합니다. 적용을 누르세요.\n${lines}` +
        (skipped ? `\n(이미 채주에 있는 ${skipped}건은 제외)` : '') +
        '\n이후 과목과 「연결」해야 임시저장됩니다.'
    );
  }

  if (!intent.vendorName || !(Number(intent.amount) > 0)) {
    persistSlots(ctx, { ...intent, kind: KIND.ADD_PAYEE });
    const missing = [];
    if (!intent.vendorName) missing.push('채주명');
    if (!(Number(intent.amount) > 0)) missing.push('금액');
    return reply(ctx, `${missing.join('·')}이(가) 필요합니다.\n예) 채주 홍길동 50,000원 추가해줘\n예) 사용일 20260130 법인카드사용내역을 채주내역에 추가해줘`);
  }
  const row = buildManualPayee(intent);
  return preview(
    ctx,
    'ADD_PAYEE',
    { payees: [row] },
    `채주내역에 수기 행을 추가합니다. 적용을 누르세요.\n· ${row.vendorName} ${money(row.amount)}\n이후 과목과 「연결」해야 임시저장됩니다.`
  );
}

function previewSave(ctx) {
  const session = loadType1Draft(dimsOf(ctx), ctx.draftUserKey);
  const sum = summarizeDraftSession(session);
  if (!sum.bundles.length) {
    return reply(ctx, '연결된 임시묶음이 없습니다. 먼저 증빙·채주를 과목에 연결해 주세요.');
  }
  const lines = sum.subjectLines
    .map((s) => `· ${s.subjectCode} ${s.subjectName || ''}: ${money(s.applyAmt)}`)
    .join('\n');
  return preview(
    ctx,
    'SAVE',
    {},
    `임시연결 ${sum.bundles.length}묶음을 하나의 지출결의로 저장합니다.\n` +
      `사업 ${session.project?.projectCode || ''} · 결의금액 ${money(sum.resolutionAmt)}\n${lines}`
  );
}

async function findResolutionTargets(ctx, intent) {
  const rows = await loadResultRows(ctx);
  const n = str(intent.resolutionNo);
  if (n) {
    const found = rows.filter(
      (r) => str(r.resolutionNo) === n || str(r.resolutionUid) === n || str(r.draftSessionUid) === n
    );
    return found;
  }
  const kw = intent.keyword || intent.projectName || intent.vendorName;
  if (kw) {
    return rows.filter((r) =>
      [r.resolutionNo, r.projectCode, r.projectName, r.deptName].some((v) => norm(v).includes(norm(kw)))
    );
  }
  return [];
}

async function previewDelete(ctx, intent, rawText) {
  const wantDraft = /임시/.test(rawText || '');
  if (wantDraft && !intent.resolutionNo) {
    const session = loadType1Draft(dimsOf(ctx), ctx.draftUserKey);
    if (!session?.draftSessionUid) return reply(ctx, '삭제할 임시저장이 없습니다.');
    return preview(ctx, 'DELETE', { draft: true, uids: [] }, `임시저장(${TYPE1_DRAFT_NO_LABEL})을 삭제합니다. 증빙·소득명세는 미결로 복귀합니다.`);
  }
  const hits = await findResolutionTargets(ctx, intent);
  if (!hits.length) {
    if (wantDraft) {
      const session = loadType1Draft(dimsOf(ctx), ctx.draftUserKey);
      if (session?.draftSessionUid) {
        return preview(ctx, 'DELETE', { draft: true, uids: [] }, `임시저장(${TYPE1_DRAFT_NO_LABEL})을 삭제합니다.`);
      }
    }
    persistSlots(ctx, { ...intent, kind: KIND.DELETE });
    return reply(ctx, '삭제할 결의번호 또는 「임시저장」을 알려 주세요.\n예) 결의번호 2026-1-1 삭제해줘');
  }
  if (hits.length > 1) {
    persistSlots(ctx, intent, { candidates: hits, pickType: 'resolution' });
    return reply(
      ctx,
      `삭제 대상이 여러 건입니다. 번호를 말해 주세요.\n${hits
        .map((r, i) => `${i + 1}. ${r.resolutionNo} ${r.projectName || r.projectCode || ''}`)
        .join('\n')}`
    );
  }
  const row = hits[0];
  if (isType1DraftRow(row)) {
    return preview(ctx, 'DELETE', { draft: true, uids: [] }, `임시저장(${TYPE1_DRAFT_NO_LABEL})을 삭제합니다.`);
  }
  return preview(
    ctx,
    'DELETE',
    { draft: false, uids: [row.resolutionUid] },
    `결의 ${row.resolutionNo} 을(를) 삭제합니다. 증빙·소득명세는 미결로 복귀합니다.`
  );
}

async function previewApprove(ctx, intent) {
  const hits = (await findResolutionTargets(ctx, intent)).filter(hasIssuedResolutionNo);
  if (!hits.length) {
    persistSlots(ctx, { ...intent, kind: KIND.APPROVE });
    return reply(ctx, '승인요청할 결의번호를 알려 주세요. 임시저장은 저장(채번) 후에만 가능합니다.');
  }
  if (hits.length > 1) {
    persistSlots(ctx, intent, { candidates: hits, pickType: 'resolution' });
    return reply(
      ctx,
      `대상이 여러 건입니다. 번호를 말해 주세요.\n${hits
        .map((r, i) => `${i + 1}. ${r.resolutionNo} ${r.projectName || ''}`)
        .join('\n')}`
    );
  }
  const row = hits[0];
  return preview(ctx, 'APPROVE', { uids: [row.resolutionUid] }, `결의 ${row.resolutionNo} 을(를) 승인요청합니다.`);
}

function execLink(ctx, payload) {
  const d = dimsOf(ctx);
  const current = loadType1Draft(d, ctx.draftUserKey);
  const linked = linkBundleToDraft(current, {
    project: { ...payload.project, fiscalYear: ctx.fiscalYear, tenantCode: ctx.tenantCode },
    subject: payload.subject,
    payees: payload.payees,
  });
  if (!linked.ok) return { ok: false, message: linked.error || '연결에 실패했습니다.' };
  const nextSession = { ...linked.session, fiscalYear: ctx.fiscalYear, tenantCode: ctx.tenantCode };
  saveType1Draft(d, ctx.draftUserKey, nextSession);
  const payees = payload.payees || [];
  occupyEvidencesDraft(
    ctx.tenantCode,
    ctx.fiscalYear,
    payees.map((p) => p.evidenceUid).filter(Boolean),
    nextSession.draftSessionUid
  );
  occupyStatementsDraft(
    d,
    payees.map((p) => p.statementUid).filter(Boolean),
    nextSession.draftSessionUid
  );
  const sum = summarizeDraftSession(nextSession);
  const uiEffects = {
    selectProject: payload.project,
    clearPayeeKeys: payload.fromScreen
      ? payees.map((p) => str(p.detailUid || p.evidenceUid || p.statementUid)).filter(Boolean)
      : [],
  };
  return {
    ok: true,
    message: `연결 완료 — 임시저장 ${TYPE1_DRAFT_NO_LABEL} · 묶음 ${sum.bundles.length}개`,
    uiEffects,
  };
}

function execAddPayee(payload) {
  return {
    ok: true,
    message: `채주 ${(payload.payees || []).length}건을 채주내역에 추가했습니다.`,
    uiEffects: { appendPayees: payload.payees || [] },
  };
}

async function execSave(ctx) {
  const d = dimsOf(ctx);
  const session = loadType1Draft(d, ctx.draftUserKey);
  const sum = summarizeDraftSession(session);
  if (!sum.bundles.length) return { ok: false, message: '연결된 임시묶음이 없습니다.' };
  const built = buildResolutionFromDraft(session, d.fiscalYear);
  if (!built?.master) return { ok: false, message: '임시묶음을 결의로 변환할 수 없습니다.' };

  let resBundle = await loadResolutionBundle(d);
  let oblBundle = await loadObligationBundle(d);
  const ensured = ensureObligationForResolution(d, built.master, oblBundle, resBundle);
  const master = {
    ...ensured.resolutionMaster,
    bundles: sum.bundles.map((b) => ({
      bundleUid: b.bundleUid,
      subjectCode: b.subjectCode,
      subjectName: b.subjectName,
      balanceUid: b.balanceUid,
      fundCode: b.fundCode,
      fundName: b.fundName,
      applyAmt: b.applyAmt,
      payees: (b.payees || []).map((p) => ({
        ...p,
        resolutionUid: ensured.resolutionMaster.resolutionUid,
      })),
    })),
  };
  oblBundle = ensured.obligationBundle || oblBundle;
  const uid = master.resolutionUid;
  let masters = [...(resBundle.masters || [])];
  const idx = masters.findIndex((m) => m.resolutionUid === uid);
  if (idx >= 0) masters[idx] = { ...masters[idx], ...master, mod: 'U' };
  else masters.push({ ...master, mod: 'A' });
  let details = (resBundle.details || []).filter((x) => x.resolutionUid !== uid);
  details = [...details, ...built.details];
  const saved = await saveResolutionBundle(d, masters, details);
  if (oblBundle) {
    await saveObligationBundle(d, oblBundle.masters, oblBundle.vendors, oblBundle.contracts);
  }
  const savedMaster = saved.masters.find((m) => m.resolutionUid === uid);
  const resNo = savedMaster?.resolutionNo || master.resolutionNo || '';
  promoteEvidencesDraftToResolution(ctx.tenantCode, ctx.fiscalYear, {
    draftSessionUid: session.draftSessionUid,
    resolutionUid: uid,
    resolutionNo: resNo,
    evidenceUids: built.details.map((x) => x.evidenceUid).filter(Boolean),
  });
  promoteStatementsDraftToResolution(d, {
    draftSessionUid: session.draftSessionUid,
    resolutionUid: uid,
    resolutionNo: resNo,
    statementUids: built.details.map((x) => x.statementUid).filter(Boolean),
  });
  clearType1Draft(d, ctx.draftUserKey);
  return { ok: true, message: `지출결의 ${resNo || uid} 을(를) 등록했습니다.` };
}

async function execDelete(ctx, payload) {
  const d = dimsOf(ctx);
  if (payload.draft) {
    const session = loadType1Draft(d, ctx.draftUserKey);
    if (session?.draftSessionUid) {
      releaseEvidencesDraft(ctx.tenantCode, ctx.fiscalYear, session.draftSessionUid);
      releaseStatementsDraft(d, session.draftSessionUid);
      clearType1Draft(d, ctx.draftUserKey);
    }
    return { ok: true, message: '임시저장을 삭제하고 증빙·소득명세 연결을 해제했습니다.' };
  }
  const uids = payload.uids || [];
  const bundle = await loadResolutionBundle(d);
  const targets = (bundle.masters || []).filter((m) => uids.includes(m.resolutionUid));
  const blocked = targets.filter(
    (m) => m.status !== EXPENSE_STATUS.DRAFT && m.status !== EXPENSE_STATUS.REJECTED
  );
  if (blocked.length) return { ok: false, message: '작성중·반려 건만 삭제할 수 있습니다.' };
  const uidSet = new Set(uids);
  const masters = (bundle.masters || []).map((m) => (uidSet.has(m.resolutionUid) ? { ...m, mod: 'D' } : m));
  const details = (bundle.details || []).map((x) => (uidSet.has(x.resolutionUid) ? { ...x, mod: 'D' } : x));
  await saveResolutionBundle(d, masters, details);
  uids.forEach((uid) => {
    releaseEvidencesByResolution(ctx.tenantCode, d.fiscalYear, uid);
    releaseStatementsByResolution(d, uid);
  });
  return { ok: true, message: '결의를 삭제하고 증빙·소득명세 연결을 해제했습니다.' };
}

async function execApprove(ctx, payload) {
  const d = dimsOf(ctx);
  let bundle = await loadResolutionBundle(d);
  const uids = payload.uids || [];
  for (const uid of uids) {
    const m = (bundle.masters || []).find((x) => x.resolutionUid === uid);
    if (!m || m.mod === 'D') continue;
    if (m.status !== EXPENSE_STATUS.DRAFT && m.status !== EXPENSE_STATUS.REJECTED) {
      return { ok: false, message: `${m.resolutionNo || uid}: 작성중·반려만 승인요청할 수 있습니다.` };
    }
    const result = requestResolutionApproval(bundle.masters, bundle.details, uid);
    bundle = { masters: result.masters, details: result.details };
  }
  await saveResolutionBundle(d, bundle.masters, bundle.details);
  return { ok: true, message: `승인요청 ${uids.length}건 처리했습니다.` };
}

async function interpret(ctx, message) {
  let intent = parseIntent(message);
  let slots = getSlots(ctx);

  if (intent.kind === KIND.UNKNOWN && slots?.kind && str(message)) {
    intent.kind = slots.kind;
    const token = stripJosa(message);
    if (intent.pickIndex == null && token) {
      if (slots.pickType === 'project' && !intent.projectCode && !intent.projectName) {
        intent.projectName = token;
      } else if (slots.pickType === 'subject' && !intent.subjectCode && !intent.subjectName) {
        intent.subjectCode = token;
      } else if (slots.pickType === 'evidence' && !intent.evidenceKeyword) {
        intent.evidenceKeyword = token;
      } else if (slots.pickType === 'statement' && !intent.evidenceKeyword) {
        intent.evidenceKeyword = token;
      } else if (slots.pickType === 'resolution' && !intent.resolutionNo) {
        intent.resolutionNo = token;
      } else if (slots.kind === KIND.LINK && !intent.evidenceKeyword && !intent.vendorName && !intent.subjectCode && !intent.projectCode) {
        intent.evidenceKeyword = token;
      } else if (slots.kind === KIND.ADD_PAYEE && !intent.vendorName) {
        const asDate = parseUseDate(token) || dateKey(token);
        if (asDate && !intent.useDate) intent.useDate = asDate;
        else intent.vendorName = token;
      }
    }
  }

  if (slots && intent.pickIndex != null) {
    const picked = applyPick(intent, { ...slots });
    if (picked.error) return reply(ctx, picked.error);
    intent = mergeIntent(picked.intent, picked.slots);
    slots = picked.slots;
    setSlots(ctx, slots);
  } else if (slots && intent.kind === slots.kind) {
    intent = mergeIntent(intent, slots);
  } else if (intent.kind !== KIND.UNKNOWN && intent.kind !== KIND.HELP) {
    /* 새 의도 — 슬롯 초기화는 각 미리보기에서 */
  }

  switch (intent.kind) {
    case KIND.HELP:
      setSlots(ctx, null);
      return help(ctx);
    case KIND.REFUSE_TENANT:
      return reply(ctx, `다른 기관 데이터는 조회·변경할 수 없습니다. 로그인 기관(${ctx.tenantCode})만 대상입니다.`);
    case KIND.LIST:
      return await listResolutions(ctx, intent);
    case KIND.LIST_EVIDENCE:
      return listEvidence(ctx, intent);
    case KIND.LIST_PROJECT:
      return listProjects(ctx, intent);
    case KIND.LIST_SUBJECT:
      return listSubjects(ctx, intent);
    case KIND.LINK:
      return previewLink(ctx, intent);
    case KIND.ADD_PAYEE:
      return previewAddPayee(ctx, intent);
    case KIND.SAVE:
      return previewSave(ctx);
    case KIND.DELETE:
      return await previewDelete(ctx, intent, message);
    case KIND.APPROVE:
      return await previewApprove(ctx, intent);
    default:
      if (slots?.kind) {
        intent.kind = slots.kind;
        intent = mergeIntent(intent, slots);
        if (intent.kind === KIND.LINK) return previewLink(ctx, intent);
        if (intent.kind === KIND.ADD_PAYEE) return previewAddPayee(ctx, intent);
        if (intent.kind === KIND.DELETE) return await previewDelete(ctx, intent, message);
        if (intent.kind === KIND.APPROVE) return await previewApprove(ctx, intent);
      }
      return help(ctx, '지출결의 조회·연결·저장·삭제만 할 수 있습니다.');
  }
}

async function executeConfirm(ctx, previewId) {
  purgeExpired();
  const p = previewId ? pendingPreviews.get(previewId) : null;
  if (!p) {
    return {
      ...base(ctx),
      success: false,
      type: 'error',
      message: '적용할 미리보기가 없거나 만료되었습니다. 명령을 다시 입력해 주세요.',
    };
  }
  if (p.tenantCode !== ctx.tenantCode || p.userKey !== ctx.draftUserKey) {
    return {
      ...base(ctx),
      success: false,
      type: 'error',
      message: '다른 사용자의 미리보기는 적용할 수 없습니다.',
    };
  }
  pendingPreviews.delete(previewId);

  let result = { ok: false, message: '처리할 수 없습니다.' };
  if (p.action === 'LINK') result = execLink(ctx, p.payload);
  else if (p.action === 'ADD_PAYEE') result = execAddPayee(p.payload);
  else if (p.action === 'SAVE') result = await execSave(ctx);
  else if (p.action === 'DELETE') result = await execDelete(ctx, p.payload);
  else if (p.action === 'APPROVE') result = await execApprove(ctx, p.payload);

  return {
    ...base(ctx),
    success: !!result.ok,
    type: result.ok ? 'done' : 'error',
    message: result.message,
    uiEffects: result.uiEffects || null,
  };
}

/**
 * @param {{ message: string, confirm?: boolean, previewId?: string, ctx: object }} args
 */
export async function handleType1Chat({ message, confirm = false, previewId = '', ctx }) {
  const safeCtx = ctx || {};
  if (!safeCtx.tenantCode) {
    return { success: false, type: 'error', message: '로그인 세션이 없습니다.', screenId: TYPE1_CHAT_SCREEN_ID };
  }
  if (!safeCtx.fiscalYear) {
    return { ...base(safeCtx), success: false, type: 'error', message: '회계연도를 입력하세요.' };
  }
  try {
    if (confirm) return await executeConfirm(safeCtx, previewId);
    return await interpret(safeCtx, message);
  } catch (e) {
    return {
      ...base(safeCtx),
      success: false,
      type: 'error',
      message: e?.message || '처리 중 오류가 발생했습니다.',
    };
  }
}
